import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Home as HomeIcon,
  Dumbbell,
  Footprints,
  Camera,
  TrendingUp,
  MessageCircle,
  Target,
  HeartPulse,
} from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { toast } from 'sonner';
import { Analytics } from '@/lib/analytics';

interface TutorialStep {
  id: number;
  /** Used for analytics + the title shown in the card */
  key: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  /** CSS selector for the bottom-nav tab to spotlight (omit for centred / no-target steps). */
  targetElement?: string;
  /** Path to navigate to so the highlighted tab matches what the user is looking at. */
  route?: string;
}

interface TutorialProps {
  onComplete: () => void;
}

/**
 * Goal-led spotlight tutorial.
 *
 * The flow tells a story rather than just labelling tabs:
 *
 *   1. Welcome - restates the user's stated goal as the destination.
 *   2. Progress - the proof that the goal is being hit.
 *   3. Apple Health - the fuel that makes coaching personal (NO spotlight,
 *      centred card; we want this one to read like a manifesto).
 *   4. Workouts - the input.
 *   5. Health (meals/sleep/water) - the other input.
 *   6. Arnold - the brain.
 *   7. Home - the daily mission control + final CTA.
 *
 * UI is a bottom-sheet style card (compact, never blocks the screen) with a
 * lightly tinted backdrop and a soft spotlight ring around the highlighted
 * tab so the user can still see the underlying app.
 */
/**
 * Map of internal goal IDs *and* the user-facing labels written by Onboarding
 * (which stores the display label, not an ID, e.g. "Build Muscle") to a
 * translation key under `components.tutorial.primaryGoals.*`. The actual
 * verb-phrase ("build muscle", "perder peso", etc.) is resolved via i18n.
 */
const PRIMARY_GOAL_KEYS: Record<string, string> = {
  weight_loss: 'lose_weight',
  weight_gain: 'build_muscle',
  muscle_gain: 'build_muscle',
  endurance: 'improve_endurance',
  strength: 'improve_strength',
  general_fitness: 'general_fitness',
  flexibility: 'improve_flexibility',
  performance: 'athletic_performance',
  health: 'general_fitness',
  // Display labels saved by Onboarding (src/pages/Onboarding.tsx primaryGoals)
  'Build Muscle': 'build_muscle',
  'Lose Weight': 'lose_weight',
  'Improve Strength': 'improve_strength',
  'Increase Endurance': 'improve_endurance',
  'Better Health': 'general_fitness',
  'Sport Performance': 'athletic_performance',
  'Flexibility': 'improve_flexibility',
  'Mental Wellness': 'stress_relief',
};

export const Tutorial = ({ onComplete }: TutorialProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, updateProfile } = useProfile();
  const { t } = useTranslation();
  const cardWrapperRef = useRef<HTMLDivElement | null>(null);

  // Build a single human-readable goal phrase that includes EVERY primary
  // goal the user picked (not just the first), then appends their specific
  // strength/endurance goals if they have any. Goal phrases are translated;
  // the joiners ("and", "hit", ", and ") come from the components.tutorial
  // namespace so each language reads naturally.
  const goalLabel = useMemo(() => {
    const joinNatural = (parts: string[]): string => {
      if (parts.length === 0) return '';
      if (parts.length === 1) return parts[0];
      const joiner = t('components.tutorial.joinerAnd');
      if (parts.length === 2) return `${parts[0]}${joiner}${parts[1]}`;
      return `${parts.slice(0, -1).join(', ')}${joiner}${parts[parts.length - 1]}`;
    };

    const primaryPhrases = (profile?.primary_goals ?? [])
      .map((g) => {
        const key = PRIMARY_GOAL_KEYS[g];
        if (key) return t(`components.tutorial.primaryGoals.${key}`);
        return g.replace(/_/g, ' ');
      })
      .filter(Boolean);

    const specificRaw = profile?.specific_goals?.trim();
    const specificPhrases = specificRaw
      ? specificRaw.split(',').map((s) => s.trim()).filter(Boolean)
      : [];

    if (primaryPhrases.length === 0 && specificPhrases.length === 0) return null;

    const primaryPart = joinNatural(primaryPhrases);
    const specificPart = specificPhrases.length > 0
      ? `${t('components.tutorial.specificGoalsPrefix')}${joinNatural(specificPhrases)}`
      : '';

    return [primaryPart, specificPart].filter(Boolean).join(t('components.tutorial.goalPartsJoiner'));
  }, [profile?.primary_goals, profile?.specific_goals, t]);

  const firstName = profile?.first_name?.trim() || '';
  /** Coach name used throughout every step's copy. */
  const coachName = profile?.ai_coach_name?.trim() || 'Arnold';

  const tutorialSteps: TutorialStep[] = useMemo(() => [
    {
      id: 1,
      key: 'welcome',
      title: firstName
        ? t('tutorial.welcome.titleWithName', { name: firstName })
        : t('tutorial.welcome.title'),
      description: goalLabel
        ? t('tutorial.welcome.descriptionWithGoal', { goal: goalLabel, coach: coachName })
        : t('tutorial.welcome.description', { coach: coachName }),
      icon: <Target className="h-7 w-7 text-primary" />,
      route: '/dashboard',
    },
    {
      id: 2,
      key: 'progress',
      title: t('tutorial.progress.title'),
      description: t('tutorial.progress.description', { coach: coachName }),
      icon: <TrendingUp className="h-7 w-7 text-primary" />,
      targetElement: '[data-tutorial="progress"]',
      route: '/progress',
    },
    {
      id: 3,
      key: 'appleHealth',
      title: t('tutorial.appleHealth.title'),
      description: t('tutorial.appleHealth.description', { coach: coachName }),
      icon: <HeartPulse className="h-7 w-7 text-primary" />,
      route: '/dashboard',
    },
    {
      id: 4,
      key: 'workouts',
      title: t('tutorial.workouts.title'),
      description: t('tutorial.workouts.description', { coach: coachName }),
      icon: (
        <div className="flex items-center gap-1">
          <Dumbbell className="h-6 w-6 text-primary" />
          <Footprints className="h-6 w-6 text-primary" />
        </div>
      ),
      targetElement: '[data-tutorial="workouts"]',
      route: '/workouts',
    },
    {
      id: 5,
      key: 'healthTab',
      title: t('tutorial.healthTab.title'),
      description: t('tutorial.healthTab.description', { coach: coachName }),
      icon: <Camera className="h-7 w-7 text-primary" />,
      targetElement: '[data-tutorial="nutrition"]',
      route: '/food-log',
    },
    {
      id: 6,
      key: 'coach',
      title: t('tutorial.coach.title', { coach: coachName }),
      description: t('tutorial.coach.description', { coach: coachName }),
      icon: <MessageCircle className="h-7 w-7 text-primary" />,
      targetElement: '[data-tutorial="chat"]',
      route: '/chat',
    },
    {
      id: 7,
      key: 'home',
      title: t('tutorial.home.title'),
      description: t('tutorial.home.description', { coach: coachName }),
      icon: <HomeIcon className="h-7 w-7 text-primary" />,
      targetElement: '[data-tutorial="dashboard"]',
      route: '/dashboard',
    },
  ], [t, firstName, goalLabel, coachName]);

  const step = tutorialSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tutorialSteps.length - 1;

  useEffect(() => {
    Analytics.tutorialStepViewed(currentStep + 1, step.key);
  }, [currentStep, step.key]);

  // Auto-switch routes per step so the user is looking at the right screen
  // when we highlight a tab.
  useEffect(() => {
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Re-measure the highlighted tab on every step / route change / window
  // resize so the spotlight stays glued to the right element even as the
  // bottom nav repaints.
  useLayoutEffect(() => {
    const measure = () => {
      if (!step.targetElement) {
        setHighlightRect(null);
        return;
      }
      const el = document.querySelector(step.targetElement);
      if (!el) {
        setHighlightRect(null);
        return;
      }
      setHighlightRect(el.getBoundingClientRect());
    };

    // Wait two frames so the route change has finished painting the new nav
    // before we measure.
    let frame1 = 0;
    let frame2 = 0;
    frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(measure);
    });

    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(frame1);
      cancelAnimationFrame(frame2);
      window.removeEventListener('resize', measure);
    };
  }, [currentStep, step.targetElement, location.pathname]);

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const finalize = async (skipped: boolean) => {
    setIsCompleting(true);
    try {
      if (skipped) Analytics.tutorialSkipped(currentStep);
      else Analytics.tutorialCompleted();
      await updateProfile({ tutorial_completed: true });
      localStorage.setItem('tutorial_completed', 'true');
      localStorage.removeItem('force_tutorial');
      if (!skipped) toast.success(t('tutorial.welcomeToast'));
      setTimeout(() => {
        onComplete();
        navigate('/dashboard', { replace: true });
      }, 120);
    } catch (error) {
      console.error('[Tutorial] Failed to finalize:', error);
      toast.error(t('tutorial.saveError'));
      setIsCompleting(false);
    }
  };

  const handleSkip = () => finalize(true);
  const handleComplete = () => finalize(false);

  // Spotlight values - expand the rect by 8px to give the highlight some
  // breathing room around the icon, then clamp the rect so that even the
  // outer 2px box-shadow ring stays fully inside the viewport. The Home tab
  // sits flush against the left edge and the Progress tab sits flush against
  // the right edge, so without this clamp the ring drawn outside the box
  // gets clipped by the screen border on those tabs.
  const PADDING = 4;
  // Total horizontal/vertical inset reserved for the ring + glow halo. The
  // ring itself is 2px outside the box; we leave more room here so edge
  // tabs (Home, Progress) never have the line clipped by the screen border.
  const VIEWPORT_INSET = 8;
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 0;
  const viewportH = typeof window !== 'undefined' ? window.innerHeight : 0;
  const spotlight = highlightRect
    ? (() => {
        let x = highlightRect.left - PADDING;
        let y = highlightRect.top - PADDING;
        let right = highlightRect.right + PADDING;
        let bottom = highlightRect.bottom + PADDING;
        // Clamp so the OUTER ring (drawn 2px past the box) fits on-screen.
        x = Math.max(VIEWPORT_INSET, x);
        y = Math.max(VIEWPORT_INSET, y);
        right = Math.min(viewportW - VIEWPORT_INSET, right);
        bottom = Math.min(viewportH - VIEWPORT_INSET, bottom);
        return { x, y, w: Math.max(0, right - x), h: Math.max(0, bottom - y) };
      })()
    : null;

  // Card position:
  //   - Spotlight steps -> bottom sheet sitting just above the highlighted
  //     bottom-nav tab so the tap target is still framed by the spotlight.
  //   - No-spotlight steps -> centred slightly above the middle so the
  //     hero icon + title get visual room without dominating the screen.
  const isCentered = !spotlight;

  return (
    <div
      className="fixed inset-0 z-[10000] pointer-events-auto"
      role="dialog"
      aria-modal="true"
      aria-label={`Tutorial: ${step.title}`}
    >
      {/* Backdrop. Lighter than before so the underlying app stays visible -
          we want users to actually see what they're being taught about.
          Tappable so a tap outside the card just dismisses the tour. */}
      <div
        className="absolute inset-0 bg-black/40 animate-fade-in"
        onClick={handleSkip}
      />

      {/* Soft spotlight ring around the highlighted tab. Uses two stacked
          box-shadows: the first is the crisp 2px ring (renders OUTSIDE the
          element, follows border-radius perfectly on every browser, never
          clipped by border-box), the second is the outer glow. We don't
          clamp the rect to the viewport - the shadow extending past the
          edge is fine and far better than shrinking the ring (which is what
          made the bottom-nav line look "broken" before). */}
      {spotlight && (
        <div
          aria-hidden
          className="absolute pointer-events-none transition-all duration-300 ease-out"
          style={{
            left: spotlight.x,
            top: spotlight.y,
            width: spotlight.w,
            height: spotlight.h,
            borderRadius: 14,
            boxShadow:
              '0 0 0 2px hsl(var(--primary)), 0 0 24px 4px hsl(var(--primary) / 0.55)',
            background: 'transparent',
          }}
        />
      )}

      {/* Card. Click events on the card itself shouldn't bubble to the
          backdrop (which would dismiss the tour). */}
      <div
        ref={cardWrapperRef}
        className="absolute left-1/2 -translate-x-1/2 w-[min(380px,calc(100vw-24px))] pointer-events-auto"
        style={{
          // Spotlight steps: dock the card above the bottom nav.
          // Centred steps: place it ~52% from the top so it never crosses
          // the safe-area at the bottom on smaller phones.
          ...(isCentered
            ? {
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }
            : {
                bottom: spotlight
                  ? `calc(${window.innerHeight - spotlight.y + 12}px)`
                  : 'calc(env(safe-area-inset-bottom, 0px) + 96px)',
                transform: 'translate(-50%, 0)',
              }),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="relative rounded-2xl border border-primary/25 shadow-2xl animate-scale-in overflow-hidden"
          style={{
            // Near-opaque so white description text is fully readable on top
            // of any underlying screen. backdrop-filter still gives the soft
            // frosted-glass feel against the dark backdrop.
            background: 'hsl(var(--card) / 0.98)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
          }}
        >
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 h-7 w-7"
            onClick={handleSkip}
            disabled={isCompleting}
            aria-label={t('tutorial.skipTour')}
          >
            <X className="h-4 w-4" />
          </Button>

          <div className="px-4 pt-4 pb-3">
            {/* Hero row: icon + step counter. Compact - we no longer waste
                vertical space on a giant logo. */}
            <div className="flex items-center gap-3 mb-2">
              <div className="flex-shrink-0 rounded-xl bg-primary/10 p-2">
                {step.icon}
              </div>
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                {t('tutorial.stepCounter', {
                  current: step.id,
                  total: tutorialSteps.length,
                })}
              </span>
            </div>

            {/* Title - tighter line-height, smaller than before. */}
            <h3 className="text-base font-bold mb-1 text-foreground pr-7 leading-snug">
              {step.title}
            </h3>

            {/* Description - solid foreground so it stays fully readable on
                top of the translucent card background (was muted-foreground
                which became unreadable against busy screens). */}
            <p className="text-[13px] text-foreground leading-relaxed mb-3">
              {step.description}
            </p>

            {/* Progress dots. */}
            <div className="flex gap-1.5 mb-3">
              {tutorialSteps.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    idx === currentStep
                      ? 'w-5 bg-primary'
                      : idx < currentStep
                      ? 'w-1 bg-primary/50'
                      : 'w-1 bg-muted'
                  }`}
                />
              ))}
            </div>

            {/* Action row */}
            <div className="flex gap-2 items-center">
              {!isFirstStep && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  disabled={isCompleting}
                  className="text-xs h-9 px-2"
                  aria-label={t('common.back')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}
              {isFirstStep && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSkip}
                  disabled={isCompleting}
                  className="text-xs h-9"
                >
                  {t('tutorial.skipTour')}
                </Button>
              )}
              <Button
                onClick={handleNext}
                disabled={isCompleting}
                size="sm"
                className="flex-1 h-9"
              >
                {isLastStep ? (
                  t('tutorial.ready')
                ) : (
                  <>
                    {t('tutorial.next')}
                    <ChevronRight className="ml-1 h-3.5 w-3.5" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
